/**
 * Pure math for the Packages & Specials editor (v3).
 *
 * These functions are the single source of truth for how builder inputs
 * (template_params) turn into the package's `items` array, its computed
 * price, and a suggested display name. They feed both the live comparison
 * in PackageEditorModal and the payload submitted to the backend, so the
 * shapes here mirror the backend contract EXACTLY:
 *
 *  - bogo_percent: {buy_service_id, buy_qty, get_service_id, get_qty, percent_off}
 *      items merge per service (same buy+get service => one item, summed qty)
 *      price = buy_qty*P(buy) + get_qty*P(get)*(1 - percent_off/100)
 *  - simple_discount: {items: [{salon_service_id, quantity}], discount_type: 'flat', discount_value}
 *      items as given
 *      price = separate - discount_value
 *
 * P(x) = the service's default_price. Prices round to 2 decimals and floor
 * at 0. Stored price+items remain authoritative on the backend; params
 * round-trip the builder. Quantities may be fractional (2dp, min 0.01).
 */

import { ServicePriceBasis } from "../../../types/context";
import {
  BogoPercentTemplateParams,
  PackageTemplateType,
  ServicePackageItemInput,
  SimpleDiscountTemplateParams,
  TemplateParams,
} from "../../../types/packages";
import {
  formatQty,
  isPerUnitBasis,
  unitLabelForBasis,
} from "../../../utils/quantityUtils";

/** "syringe" -> "Syringe" (capitalized unit noun for the suggested name). */
function capitalize(word: string): string {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

const isBogo = (params: TemplateParams): params is BogoPercentTemplateParams =>
  "buy_service_id" in params;

/**
 * Merge buy/get rows into package items. When buy and get reference the
 * same service the two quantities collapse into a single item.
 */
function bogoItems(
  params: BogoPercentTemplateParams,
): ServicePackageItemInput[] {
  if (params.buy_service_id === params.get_service_id) {
    return [
      {
        salon_service_id: params.buy_service_id,
        quantity: round2(params.buy_qty + params.get_qty),
      },
    ];
  }
  return [
    { salon_service_id: params.buy_service_id, quantity: params.buy_qty },
    { salon_service_id: params.get_service_id, quantity: params.get_qty },
  ];
}

/** Derive the package's items array from template params. */
export function computeItems(
  type: PackageTemplateType,
  params: TemplateParams | null,
): ServicePackageItemInput[] {
  if (!params) return [];
  if (type === "bogo_percent") {
    return bogoItems(params as BogoPercentTemplateParams);
  }
  return (params as SimpleDiscountTemplateParams).items.map((item) => ({
    salon_service_id: item.salon_service_id,
    quantity: item.quantity,
  }));
}

/**
 * Full price of the offer if every unit were bought separately at the
 * services' default prices — the "If bought separately" comparison line.
 * Missing services count as $0 so a partially filled builder still previews.
 */
export function separatePrice(
  params: TemplateParams | null,
  priceByServiceId: Record<string, number>,
): number {
  if (!params) return 0;
  const priceOf = (serviceId: string) => priceByServiceId[serviceId] ?? 0;
  if (isBogo(params)) {
    return round2(
      params.buy_qty * priceOf(params.buy_service_id) +
        params.get_qty * priceOf(params.get_service_id),
    );
  }
  return round2(
    params.items.reduce(
      (sum, item) => sum + item.quantity * priceOf(item.salon_service_id),
      0,
    ),
  );
}

/**
 * Discounted (final) price for the offer, rounded to 2 decimals and
 * floored at 0.
 */
export function computePrice(
  type: PackageTemplateType,
  params: TemplateParams | null,
  priceByServiceId: Record<string, number>,
): number {
  if (!params) return 0;
  const priceOf = (serviceId: string) => priceByServiceId[serviceId] ?? 0;
  if (type === "bogo_percent") {
    const p = params as BogoPercentTemplateParams;
    const price =
      p.buy_qty * priceOf(p.buy_service_id) +
      p.get_qty * priceOf(p.get_service_id) * (1 - p.percent_off / 100);
    return Math.max(0, round2(price));
  }
  const p = params as SimpleDiscountTemplateParams;
  const separate = separatePrice(p, priceByServiceId);
  const price = separate - p.discount_value;
  return Math.max(0, round2(price));
}

/**
 * Suggested display name for the offer (prefills Name until the operator
 * types their own). bogo_percent with percent_off === 100 reads "Free".
 */
export function suggestName(
  type: PackageTemplateType,
  params: TemplateParams | null,
  nameByServiceId: Record<string, string>,
  basisByServiceId: Record<string, ServicePriceBasis | undefined> = {},
): string {
  if (!params) return "";
  const nameOf = (serviceId: string) => nameByServiceId[serviceId] ?? "";
  if (type === "bogo_percent") {
    const p = params as BogoPercentTemplateParams;
    const buyName = nameOf(p.buy_service_id);
    const getName = nameOf(p.get_service_id);
    // Need both services chosen before a name reads sensibly.
    if (!buyName || !getName) return "";
    // "Get 2 Syringes Botox" — the unit word (pluralized by the get qty)
    // only appears for per-syringe/per-unit services.
    const getBasis = basisByServiceId[p.get_service_id];
    const unit = isPerUnitBasis(getBasis)
      ? `${capitalize(unitLabelForBasis(getBasis, p.get_qty))} `
      : "";
    const offer =
      p.percent_off === 100 ? "Free" : `${formatQty(p.percent_off)}% Off`;
    return (
      `Buy ${formatQty(p.buy_qty)} ${buyName}, ` +
      `Get ${formatQty(p.get_qty)} ${unit}${getName} ${offer}`
    );
  }
  const p = params as SimpleDiscountTemplateParams;
  const firstName = p.items
    .map((item) => nameOf(item.salon_service_id))
    .find(Boolean);
  if (!firstName) return "";
  const isBundle =
    p.items.length > 1 || p.items.some((item) => item.quantity > 1);
  return isBundle ? `${firstName} Bundle` : `Discounted ${firstName}`;
}
